/**
 * 이해관계자 화면의 목업 사례.
 * 고른 동안만 이 화면에 겹쳐 보이고, 플랜에는 저장하지 않는다.
 */
import { useSyncExternalStore } from "react";
import type { RaciAssignment, RaciLetter, Stakeholder } from "@/engine/types";

export const STAKEHOLDER_MOCK_CASES = [
  { id: "many", label: "많이" },
  { id: "few", label: "적게" },
  { id: "empty", label: "없음" },
] as const;

export type StakeholderMockCase = "live" | (typeof STAKEHOLDER_MOCK_CASES)[number]["id"];

export interface StakeholderMock {
  stakeholders: Stakeholder[];
  raci: RaciAssignment[];
}

const LONG_NAME = "2026년 양산 라인 투자와 안전 인증을 승인하는 공장 스폰서";

const FEW: Stakeholder[] = [
  { id: "mock:client", name: "박공장", org: "한빛로보틱스", role: "고객 PM", interest: "high", influence: "high" },
  { id: "mock:pm", name: "한로봇", org: "웨이브엑스", role: "수행 PM", interest: "high", influence: "medium" },
  { id: "mock:safety", name: "오품질", org: "한빛로보틱스", role: "안전", interest: "medium", influence: "high" },
];

const MANY: Stakeholder[] = [
  FEW[0]!,
  { id: "mock:sponsor", name: LONG_NAME, org: "한빛로보틱스", role: "스폰서", interest: "high", influence: "high" },
  FEW[1]!,
  FEW[2]!,
  { id: "mock:ctrl", name: "백제어", org: "웨이브엑스", role: "제어", interest: "medium", influence: "medium" },
  { id: "mock:vision", name: "나비전", org: "웨이브엑스", role: "비전", interest: "medium", influence: "low" },
  { id: "mock:vendor", name: "강설비", org: "세진오토메이션", role: "설비 협력", interest: "low", influence: "medium" },
  { id: "mock:floor", name: "노현장", org: "한빛로보틱스", role: "현장 반장", interest: "high", influence: "low" },
];

/** 적게. 한 줄에 책임은 한 명이고, 수행·자문·공유가 번갈아 보인다. */
const FEW_ROWS: RaciLetter[][] = [
  ["A", "R", "C"],
  ["I", "A", "R"],
  ["C", "I", "A"],
];

const MOCK_CASE_KEY = "stakeholder-mock-case";

function readStoredCase(): StakeholderMockCase {
  try {
    const raw = sessionStorage.getItem(MOCK_CASE_KEY);
    if (raw === "many" || raw === "few" || raw === "empty" || raw === "live") return raw;
  } catch {
    // 이 환경에는 sessionStorage가 없다.
  }
  return "live";
}

let current: StakeholderMockCase = readStoredCase();
const listeners = new Set<() => void>();

export function getStakeholderMockCase(): StakeholderMockCase {
  return current;
}

export function setStakeholderMockCase(next: StakeholderMockCase) {
  if (current === next) return;
  current = next;
  try {
    sessionStorage.setItem(MOCK_CASE_KEY, next);
  } catch {
    // 저장하지 못해도 이번 화면에는 적용한다.
  }
  for (const listener of listeners) listener();
}

export function useStakeholderMockCase(): StakeholderMockCase {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getStakeholderMockCase,
    getStakeholderMockCase,
  );
}

export function stakeholderMock(scenario: Exclude<StakeholderMockCase, "live">, taskIds: readonly string[]): StakeholderMock {
  if (scenario === "empty") return { stakeholders: [], raci: [] };
  const people = scenario === "few" ? FEW : MANY;
  return {
    stakeholders: people.map((person) => ({ ...person })),
    raci: scenario === "few" ? fewRaci(taskIds) : manyRaci(people, taskIds),
  };
}

function fewRaci(taskIds: readonly string[]): RaciAssignment[] {
  const out: RaciAssignment[] = [];
  taskIds.forEach((taskId, index) => {
    const row = FEW_ROWS[index % FEW_ROWS.length]!;
    row.forEach((letter, person) => {
      const stakeholder = FEW[person];
      if (stakeholder) out.push({ stakeholderId: stakeholder.id, taskId, letter });
    });
  });
  return out;
}

function manyRaci(people: readonly Stakeholder[], taskIds: readonly string[]): RaciAssignment[] {
  const letters: RaciLetter[] = ["A", "R", "C", "I"];
  const out: RaciAssignment[] = [];
  taskIds.forEach((taskId, index) => {
    letters.forEach((letter, offset) => {
      const person = people[(index + offset) % people.length];
      if (person) out.push({ stakeholderId: person.id, taskId, letter });
    });
  });
  return out;
}
